// M8 – Chiết khấu & khuyến mãi (6.4): Chương trình KM · Voucher · Hạn mức chiết khấu & cấu hình POS
import { useMemo, useState, type ReactNode } from 'react';
import { DatePicker, Drawer, Form, Input, InputNumber, Modal, Progress, Radio, Select, Switch, Table, Tabs, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { KeyRound, Pencil, Plus, Shuffle } from 'lucide-react';
import { CATALOG, type DiscountType, type PosRole, type Promotion, type Voucher } from './data';
import { promotionStatus } from './pricing';
import usePosStore from './store';
import { BRAND, branchName, branchOptions, fmtDateTime, money, usePosAuth } from './utils';
import PosShell, { ROLE_LABEL } from './components/PosShell';

const PROMO_STATUS = {
  upcoming: { label: 'Sắp diễn ra', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  running: { label: 'Đang chạy', cls: 'bg-green-50 text-green-700 border-green-200' },
  ended: { label: 'Đã kết thúc', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  off: { label: 'Tắt', cls: 'bg-red-50 text-red-600 border-red-200' },
};
const Pill = ({ label, cls }: { label: string; cls: string }) => <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
const valueText = (t: DiscountType, v: number, max?: number) => (t === '%' ? `${v}%${max ? ` (tối đa ${money(max)})` : ''}` : `${money(v)}đ`);

export default function PosPromotions() {
  const { can, role } = usePosAuth();
  return (
    <PosShell title="Khuyến mãi & chiết khấu" subtitle="Thay đổi áp dụng ngay cho màn bán hàng, không cần tải lại trang.">
      <Tabs items={[
        { key: 'promo', label: 'Chương trình khuyến mãi', children: <PromotionTab canEdit={can('edit')} /> },
        { key: 'voucher', label: 'Voucher', children: <VoucherTab canEdit={can('edit')} /> },
        { key: 'limit', label: 'Hạn mức chiết khấu & cấu hình', children: <LimitTab isAdmin={role === 'admin'} canApprove={can('approve')} /> },
      ]} />
    </PosShell>
  );
}

// ---------------------------------------------------------------------------

type PromoForm = {
  name: string; type: 'item' | 'order'; valueType: DiscountType; value: number; maxDiscount?: number; minOrder?: number;
  itemIds: string[]; allBranches: boolean; branchIds: string[]; period: [Dayjs, Dayjs]; stackable: boolean; priority: number; enabled: boolean;
};

function PromotionTab({ canEdit }: { canEdit: boolean }) {
  const promotions = usePosStore(s => s.promotions);
  const upsert = usePosStore(s => s.upsertPromotion);
  const [editing, setEditing] = useState<Promotion | 'new' | null>(null);
  const [form] = Form.useForm<PromoForm>();
  const type = Form.useWatch('type', form);
  const valueType = Form.useWatch('valueType', form);
  const allBranches = Form.useWatch('allBranches', form);

  const current = editing && editing !== 'new' ? editing : null;
  // K1: chương trình đang chạy chỉ sửa Tên, Ngày kết thúc, Trạng thái
  const locked = current ? promotionStatus(current) === 'running' : false;

  const openForm = (p: Promotion | 'new') => {
    setEditing(p);
    const now = dayjs();
    form.setFieldsValue(p === 'new'
      ? { name: '', type: 'item', valueType: '%', value: 10, itemIds: [], allBranches: true, branchIds: [], period: [now.startOf('day'), now.add(30, 'day').endOf('day')], stackable: false, priority: 50, enabled: true }
      : { ...p, allBranches: p.branchIds === 'all', branchIds: p.branchIds === 'all' ? [] : p.branchIds, period: [dayjs(p.startAt), dayjs(p.endAt)] });
  };

  const save = async () => {
    const v = await form.validateFields();
    const p: Promotion = {
      id: current?.id || `km${Date.now()}`, name: v.name.trim(), type: v.type, valueType: v.valueType, value: v.value,
      maxDiscount: v.valueType === '%' ? v.maxDiscount || undefined : undefined, minOrder: v.type === 'order' ? v.minOrder : undefined,
      itemIds: v.type === 'item' ? v.itemIds : [], branchIds: v.allBranches ? 'all' : v.branchIds,
      startAt: v.period[0].toISOString(), endAt: v.period[1].toISOString(), stackable: v.stackable, priority: v.priority, enabled: v.enabled,
      usedCount: current?.usedCount || 0,
    };
    upsert(p);
    setEditing(null);
    message.success(current ? 'Đã cập nhật chương trình' : 'Đã thêm chương trình');
  };

  const columns: ColumnsType<Promotion> = [
    { title: 'Tên', width: 220, render: (_, p) => <b>{p.name}</b> },
    { title: 'Loại', width: 120, render: (_, p) => (p.type === 'item' ? 'Theo dòng hàng' : 'Theo đơn') },
    { title: 'Giá trị', width: 160, render: (_, p) => valueText(p.valueType, p.value, p.maxDiscount) },
    { title: 'Điều kiện', width: 220, render: (_, p) => (p.type === 'order' ? `Đơn từ ${money(p.minOrder || 0)}đ` : p.itemIds.map(id => CATALOG.find(c => c.id === id)?.name).join(', ')) },
    { title: 'Chi nhánh', width: 150, render: (_, p) => (p.branchIds === 'all' ? 'Tất cả' : p.branchIds.map(branchName).join(', ')) },
    { title: 'Hiệu lực', width: 200, render: (_, p) => <span className="text-xs">{fmtDateTime(p.startAt)} → {fmtDateTime(p.endAt)}</span> },
    { title: 'Cộng dồn', width: 90, render: (_, p) => (p.stackable ? 'Có' : 'Không') },
    { title: 'Ưu tiên', width: 80, align: 'right', dataIndex: 'priority' },
    { title: 'Trạng thái', width: 120, render: (_, p) => <Pill {...PROMO_STATUS[promotionStatus(p)]} /> },
    { title: 'Đã áp dụng', width: 100, align: 'right', dataIndex: 'usedCount' },
    {
      title: '', width: 120, fixed: 'right',
      render: (_, p) => canEdit && (
        <div className="flex items-center justify-end gap-2">
          <Switch size="small" checked={p.enabled} onChange={on => { upsert({ ...p, enabled: on }); message.success(on ? 'Đã bật' : 'Đã tắt'); }} />
          <button onClick={() => openForm(p)} className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-slate-100" aria-label="Sửa"><Pencil className="h-4 w-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <>
      {canEdit && (
        <div className="mb-3 flex justify-end">
          <button onClick={() => openForm('new')} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#357f96] px-3 text-sm font-semibold text-white hover:bg-[#2c6b7f]"><Plus className="h-4 w-4" /> Thêm chương trình</button>
        </div>
      )}
      <Table rowKey="id" size="middle" className="custom-ant-table" columns={columns} dataSource={promotions} scroll={{ x: 'max-content' }} pagination={false} />
      <p className="mt-2 text-xs text-slate-500">Không xóa chương trình đã được áp dụng – chỉ tắt (K1). Hai chương trình không cộng dồn cùng áp cho một hàng: chỉ chương trình ưu tiên cao hơn được áp (P3).</p>

      <Drawer open={!!editing} onClose={() => setEditing(null)} width={520} title={current ? 'Sửa chương trình' : 'Thêm chương trình khuyến mãi'} destroyOnHidden
        extra={<button onClick={save} className="h-9 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white hover:bg-[#2c6b7f]">Lưu</button>}>
        {locked && <div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">Chương trình đang chạy chỉ sửa được Tên, Ngày kết thúc và Trạng thái.</div>}
        <Form form={form} layout="vertical" requiredMark>
          <Form.Item name="name" label="Tên chương trình" rules={[{ required: true, min: 3, max: 120, whitespace: true, message: 'Tên 3–120 ký tự' }]}><Input /></Form.Item>
          <Form.Item name="type" label="Loại" rules={[{ required: true }]}>
            <Radio.Group disabled={locked} options={[{ value: 'item', label: 'Theo dòng hàng' }, { value: 'order', label: 'Theo đơn' }]} optionType="button" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="valueType" label="Kiểu giá trị"><Select disabled={locked} options={[{ value: '%', label: 'Phần trăm (%)' }, { value: 'đ', label: 'Số tiền (đ)' }]} /></Form.Item>
            <Form.Item name="value" label="Giá trị" rules={[{ required: true, message: 'Nhập giá trị' }, { validator: (_, v) => (valueType === '%' ? (v >= 1 && v <= 100) : v > 0) ? Promise.resolve() : Promise.reject(valueType === '%' ? '1–100%' : 'Lớn hơn 0') }]}>
              <InputNumber disabled={locked} className="!w-full" min={0} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} />
            </Form.Item>
          </div>
          {valueType === '%' && (
            <Form.Item name="maxDiscount" label="Giảm tối đa (đ)"><InputNumber disabled={locked} className="!w-full" min={0} formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} /></Form.Item>
          )}
          {type === 'item' ? (
            <Form.Item name="itemIds" label="Áp dụng cho" rules={[{ required: true, type: 'array', min: 1, message: 'Chọn ít nhất 1 dịch vụ / sản phẩm' }]}>
              <Select disabled={locked} mode="multiple" optionFilterProp="label" options={CATALOG.map(c => ({ value: c.id, label: c.name }))} />
            </Form.Item>
          ) : (
            <Form.Item name="minOrder" label="Đơn tối thiểu (đ)" rules={[{ required: true, message: 'Nhập giá trị đơn tối thiểu' }, { type: 'number', min: 1, message: 'Lớn hơn 0' }]}>
              <InputNumber disabled={locked} className="!w-full" formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} />
            </Form.Item>
          )}
          <Form.Item name="allBranches" label="Chi nhánh" valuePropName="checked"><Switch disabled={locked} checkedChildren="Tất cả" unCheckedChildren="Chọn" /></Form.Item>
          {!allBranches && (
            <Form.Item name="branchIds" rules={[{ required: true, type: 'array', min: 1, message: 'Chọn chi nhánh' }]}><Select disabled={locked} mode="multiple" options={branchOptions} /></Form.Item>
          )}
          <Form.Item name="period" label="Hiệu lực" rules={[{ required: true }, { validator: (_, v) => (v && v[1].isAfter(v[0]) ? Promise.resolve() : Promise.reject('Kết thúc phải sau bắt đầu')) }]}>
            <DatePicker.RangePicker showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" className="!w-full" disabled={locked ? [true, false] : false} />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="stackable" label="Cộng dồn" valuePropName="checked"><Switch disabled={locked} /></Form.Item>
            <Form.Item name="priority" label="Ưu tiên (1–100)" rules={[{ type: 'number', min: 1, max: 100 }]}><InputNumber disabled={locked} className="!w-full" /></Form.Item>
          </div>
          <Form.Item name="enabled" label="Trạng thái" valuePropName="checked"><Switch checkedChildren="Bật" unCheckedChildren="Tắt" /></Form.Item>
        </Form>
      </Drawer>
    </>
  );
}

// ---------------------------------------------------------------------------

type VoucherForm = Omit<Voucher, 'expiresAt' | 'used'> & { expiresAt: Dayjs };

function VoucherTab({ canEdit }: { canEdit: boolean }) {
  const vouchers = usePosStore(s => s.vouchers);
  const upsert = usePosStore(s => s.upsertVoucher);
  const [editing, setEditing] = useState<Voucher | 'new' | null>(null);
  const [form] = Form.useForm<VoucherForm>();
  const valueType = Form.useWatch('valueType', form);
  const current = editing && editing !== 'new' ? editing : null;

  const state = (v: Voucher) => {
    if (!v.enabled) return PROMO_STATUS.off;
    if (dayjs(v.expiresAt).isBefore(dayjs())) return { label: 'Hết hạn', cls: PROMO_STATUS.ended.cls };
    if (v.used >= v.usageLimit) return { label: 'Hết lượt', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    return { label: 'Hiệu lực', cls: PROMO_STATUS.running.cls };
  };

  const openForm = (v: Voucher | 'new') => {
    setEditing(v);
    form.setFieldsValue(v === 'new'
      ? { code: '', name: '', valueType: 'đ', value: 100_000, minOrder: 0, expiresAt: dayjs().add(30, 'day').endOf('day'), usageLimit: 100, enabled: true }
      : { ...v, expiresAt: dayjs(v.expiresAt) });
  };

  const save = async () => {
    const f = await form.validateFields();
    upsert({ ...f, code: f.code.toUpperCase(), maxDiscount: f.valueType === '%' ? f.maxDiscount || undefined : undefined, expiresAt: f.expiresAt.toISOString(), used: current?.used || 0 }, current?.code);
    setEditing(null);
    message.success(current ? 'Đã cập nhật voucher' : 'Đã tạo voucher');
  };

  const columns: ColumnsType<Voucher> = [
    { title: 'Mã', width: 130, render: (_, v) => <span className="font-mono font-bold">{v.code}</span> },
    { title: 'Tên', width: 220, dataIndex: 'name' },
    { title: 'Giá trị', width: 170, render: (_, v) => valueText(v.valueType, v.value, v.maxDiscount) },
    { title: 'Đơn tối thiểu', width: 120, align: 'right', render: (_, v) => money(v.minOrder) },
    { title: 'Hạn dùng', width: 150, render: (_, v) => fmtDateTime(v.expiresAt) },
    { title: 'Đã dùng / Giới hạn', width: 170, render: (_, v) => <div><span className="text-xs">{v.used} / {v.usageLimit}</span><Progress percent={Math.round((v.used / v.usageLimit) * 100)} size="small" showInfo={false} strokeColor={BRAND.primary} /></div> },
    { title: 'Trạng thái', width: 110, render: (_, v) => <Pill {...state(v)} /> },
    {
      title: '', width: 110, fixed: 'right',
      render: (_, v) => canEdit && (
        <div className="flex items-center justify-end gap-2">
          <Switch size="small" checked={v.enabled} onChange={on => upsert({ ...v, enabled: on })} />
          <button onClick={() => openForm(v)} className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-slate-100" aria-label="Sửa"><Pencil className="h-4 w-4" /></button>
        </div>
      ),
    },
  ];

  const randomCode = () => form.setFieldValue('code', `KT${Math.random().toString(36).slice(2, 8).toUpperCase()}`);

  return (
    <>
      {canEdit && (
        <div className="mb-3 flex justify-end">
          <button onClick={() => openForm('new')} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#357f96] px-3 text-sm font-semibold text-white hover:bg-[#2c6b7f]"><Plus className="h-4 w-4" /> Tạo voucher</button>
        </div>
      )}
      <Table rowKey="code" size="middle" className="custom-ant-table" columns={columns} dataSource={vouchers} scroll={{ x: 'max-content' }} pagination={false} />
      <Modal open={!!editing} onCancel={() => setEditing(null)} onOk={save} okText="Lưu" cancelText="Hủy" title={current ? `Sửa voucher ${current.code}` : 'Tạo voucher'} destroyOnHidden centered>
        <Form form={form} layout="vertical">
          <Form.Item label="Mã voucher" required>
            <div className="flex gap-2">
              <Form.Item name="code" noStyle rules={[
                { required: true, pattern: /^[A-Za-z0-9]{4,20}$/, message: '4–20 ký tự chữ / số' },
                { validator: (_, v) => (v && vouchers.some(x => x.code === String(v).toUpperCase() && x.code !== current?.code) ? Promise.reject('Mã đã tồn tại') : Promise.resolve()) },
              ]}>
                <Input className="font-mono uppercase" disabled={!!current && current.used > 0} />
              </Form.Item>
              {!current && <button type="button" onClick={randomCode} className="flex h-8 shrink-0 items-center gap-1 rounded-lg border border-slate-300 px-2 text-sm"><Shuffle className="h-4 w-4" /> Tạo mã</button>}
            </div>
          </Form.Item>
          <Form.Item name="name" label="Tên" rules={[{ required: true, whitespace: true }]}><Input maxLength={120} /></Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="valueType" label="Kiểu"><Select options={[{ value: '%', label: '%' }, { value: 'đ', label: 'Số tiền' }]} /></Form.Item>
            <Form.Item name="value" label="Giá trị" rules={[{ required: true }, { validator: (_, v) => (valueType === '%' ? v >= 1 && v <= 100 : v > 0) ? Promise.resolve() : Promise.reject(valueType === '%' ? '1–100%' : 'Lớn hơn 0') }]}>
              <InputNumber className="!w-full" min={0} formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} />
            </Form.Item>
          </div>
          {valueType === '%' && <Form.Item name="maxDiscount" label="Giảm tối đa (đ)"><InputNumber className="!w-full" min={0} formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} /></Form.Item>}
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="minOrder" label="Đơn tối thiểu (đ)"><InputNumber className="!w-full" min={0} formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} /></Form.Item>
            <Form.Item name="usageLimit" label="Giới hạn lượt dùng" rules={[{ required: true }, { type: 'number', min: Math.max(1, current?.used || 0), message: `Không nhỏ hơn số đã dùng (${current?.used || 0})` }]}><InputNumber className="!w-full" /></Form.Item>
          </div>
          <Form.Item name="expiresAt" label="Hạn dùng" rules={[{ required: true }]}><DatePicker showTime={{ format: 'HH:mm' }} format="DD/MM/YYYY HH:mm" className="!w-full" /></Form.Item>
          <Form.Item name="enabled" label="Trạng thái" valuePropName="checked"><Switch checkedChildren="Bật" unCheckedChildren="Tắt" /></Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------

function LimitTab({ isAdmin, canApprove }: { isAdmin: boolean; canApprove: boolean }) {
  const config = usePosStore(s => s.config);
  const setConfig = usePosStore(s => s.setConfig);
  const managers = usePosStore(s => s.managers);
  const setManagerPin = usePosStore(s => s.setManagerPin);
  const resetDemo = usePosStore(s => s.resetDemo);
  const [pinFor, setPinFor] = useState<string | null>(null);
  const [pins, setPins] = useState({ old: '', next: '', confirm: '' });
  const pinManager = managers.find(m => m.id === pinFor);
  const roles: PosRole[] = ['cashier', 'manager', 'admin'];
  const pinError = useMemo(() => {
    if (!pinManager) return '';
    if (pins.old && pins.old !== pinManager.pin) return 'PIN hiện tại không đúng';
    if (pins.next && !/^\d{6}$/.test(pins.next)) return 'PIN mới gồm 6 chữ số';
    if (pins.confirm && pins.confirm !== pins.next) return 'Nhập lại PIN không khớp';
    return '';
  }, [pins, pinManager]);

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-1 font-bold">Hạn mức chiết khấu thủ công</h3>
        <p className="mb-3 text-sm text-slate-500">Chiết khấu thủ công / tạm tính sau KM tự động. Vượt hạn mức thì cần quản lý nhập PIN. {!isAdmin && 'Chỉ admin sửa được bảng này.'}</p>
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500"><tr><th className="py-2 text-left">Vai trò</th><th className="py-2 text-left">Hạn mức</th><th className="py-2 text-left">Được tự duyệt</th></tr></thead>
          <tbody>
            {roles.map(r => (
              <tr key={r} className="border-t border-slate-100">
                <td className="py-2 font-medium">{ROLE_LABEL[r]}</td>
                <td className="py-2">
                  <InputNumber disabled={!isAdmin || r === 'admin'} min={0} max={100} value={config.discountLimit[r]} addonAfter="%"
                    onChange={v => v !== null && setConfig({ discountLimit: { ...config.discountLimit, [r]: v } })} />
                </td>
                <td className="py-2 text-slate-500">{r === 'cashier' ? 'Không' : 'Có (pos.approve)'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 font-bold">Cấu hình POS</h3>
        <div className="space-y-3 text-sm">
          <Setting label="Cho phép ghi nợ" hint="Bán nợ chỉ cho khách đã chọn hồ sơ">
            <Switch disabled={!isAdmin} checked={config.allowDebt} onChange={v => setConfig({ allowDebt: v })} />
          </Setting>
          <Setting label="Chặn bán vượt tồn" hint="Tắt: chỉ cảnh báo, vẫn cho bán">
            <Switch disabled={!isAdmin} checked={config.blockOutOfStock} onChange={v => setConfig({ blockOutOfStock: v })} />
          </Setting>
          <Setting label="Ngưỡng duyệt hoàn tiền">
            <InputNumber disabled={!isAdmin} min={0} step={500000} value={config.refundApprovalThreshold} addonAfter="đ" className="w-48"
              formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))}
              onChange={v => v !== null && setConfig({ refundApprovalThreshold: v })} />
          </Setting>
          <Setting label="Làm tròn tổng đơn">
            <Select disabled={!isAdmin} className="w-40" value={config.roundingUnit} onChange={v => setConfig({ roundingUnit: v })}
              options={[{ value: 1, label: 'Không làm tròn' }, { value: 100, label: '100đ' }, { value: 500, label: '500đ' }, { value: 1000, label: '1.000đ' }]} />
          </Setting>
          <Setting label="Đơn nháp hết hạn sau">
            <InputNumber disabled={!isAdmin} min={1} max={30} value={config.draftTtlDays} addonAfter="ngày" onChange={v => v && setConfig({ draftTtlDays: v })} />
          </Setting>
          <Setting label="Tài khoản nhận VietQR">
            <div className="flex gap-2">
              <Input disabled={!isAdmin} className="w-20" value={config.bank.bankId} onChange={e => setConfig({ bank: { ...config.bank, bankId: e.target.value.toUpperCase() } })} />
              <Input disabled={!isAdmin} className="w-40" value={config.bank.accountNo} onChange={e => setConfig({ bank: { ...config.bank, accountNo: e.target.value.replace(/\D/g, '') } })} />
            </div>
          </Setting>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 xl:col-span-2">
        <h3 className="mb-1 font-bold">Mã PIN duyệt của quản lý</h3>
        <p className="mb-3 text-sm text-slate-500">Quản lý đặt / đổi PIN 6 số của chính mình. Khi có backend, PIN chỉ lưu dạng băm phía server.</p>
        <div className="flex flex-wrap gap-2">
          {managers.map(m => (
            <div key={m.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
              <span className="font-medium">{m.name}</span>
              <span className="text-xs text-slate-400">{m.branchIds.map(branchName).join(', ')}</span>
              {canApprove && <button onClick={() => { setPinFor(m.id); setPins({ old: '', next: '', confirm: '' }); }} className="flex items-center gap-1 text-sm font-semibold text-[#2c6b7f]"><KeyRound className="h-4 w-4" /> Đổi PIN</button>}
            </div>
          ))}
        </div>
        {process.env.NODE_ENV === 'development' && isAdmin && (
          <button onClick={() => Modal.confirm({ title: 'Khôi phục dữ liệu mẫu POS?', content: 'Xóa toàn bộ đơn, giao dịch, phiếu hoàn, nháp đã tạo khi thử nghiệm.', okText: 'Khôi phục', okButtonProps: { danger: true }, cancelText: 'Hủy', onOk: () => { resetDemo(); message.success('Đã khôi phục dữ liệu mẫu'); } })}
            className="mt-4 text-xs text-red-500 underline">Khôi phục dữ liệu mẫu POS (chỉ môi trường dev)</button>
        )}
      </section>

      <Modal open={!!pinManager} title={`Đổi PIN – ${pinManager?.name}`} onCancel={() => setPinFor(null)} okText="Lưu PIN" cancelText="Hủy" centered width={380}
        okButtonProps={{ disabled: !!pinError || !pins.old || !pins.next || pins.next !== pins.confirm }}
        onOk={() => { if (pinManager) { setManagerPin(pinManager.id, pins.next); setPinFor(null); message.success('Đã đổi PIN'); } }}>
        <div className="space-y-3">
          {(['old', 'next', 'confirm'] as const).map(k => (
            <label key={k} className="block text-sm">
              <span className="mb-1 block font-medium">{k === 'old' ? 'PIN hiện tại' : k === 'next' ? 'PIN mới (6 số)' : 'Nhập lại PIN mới'}</span>
              <Input.Password inputMode="numeric" maxLength={6} value={pins[k]} onChange={e => setPins(p => ({ ...p, [k]: e.target.value.replace(/\D/g, '') }))} />
            </label>
          ))}
          {pinError && <p className="text-sm text-red-600">{pinError}</p>}
        </div>
      </Modal>
    </div>
  );
}

const Setting = ({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3 last:border-0">
    <div><div className="font-medium text-slate-700">{label}</div>{hint && <div className="text-xs text-slate-400">{hint}</div>}</div>
    {children}
  </div>
);
